import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from 'react';
import * as React from 'react';
import { useMedia } from 'react-use';
import { X as XIcon } from '@hungryroot/icons';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  Button,
  type StyleVariant as ButtonStyleVariant,
} from '../../Button/Button';
import { Heading } from '../../typography/Heading/Heading';
import { Paragraph } from '../../typography/Paragraph/Paragraph';
import { SheetOrModal } from '../SheetOrModal/SheetOrModal';
import styles from './StyledSheetOrModal.module.css';

interface Props {
  testid?: string;
  onDismiss: () => void;
  onActionClick?: (e: React.MouseEvent<HTMLElement>) => void;
  body?: string | ReactNode;
  heading?: string;
  header?: ReactNode;
  confirmButtonText?: string;
  secondaryButtonText?: string;
  tertiaryButtonText?: string;
  tertiaryButtonAction?: () => void;
  ariaLabel: string;
  isOpen?: boolean;
  className?: string;
  showXButton?: boolean;
  isSubmitting?: boolean;
  size?: SizeVariant;
  rightIcon?: ReactNode;
  leftIcon?: ReactNode;
  onLeftIconClick?: () => void;
  onRightIconClick?: () => void;
  showTouchBar?: boolean;
  stackMobileButtons?: boolean;
  buttonStyleVariant?: ButtonStyleVariant;
  headingClassName?: string;
}

type SizeVariant = 'small' | 'medium' | 'large';

const getVariantClass = (size: SizeVariant) => {
  return [styles[`${size}`]].join(' ');
};

export function StyledSheetOrModal(props: Props) {
  const {
    testid,
    onDismiss,
    body,
    heading,
    header,
    confirmButtonText,
    tertiaryButtonText,
    tertiaryButtonAction,
    ariaLabel,
    isOpen = false,
    className,
    showXButton,
    isSubmitting,
    size = 'medium',
    rightIcon,
    leftIcon,
    secondaryButtonText,
    onLeftIconClick,
    onRightIconClick,
    showTouchBar = true,
    stackMobileButtons = true,
    onActionClick,
    buttonStyleVariant = 'default',
    headingClassName,
  } = props;
  const isDesktop = useMedia(BreakpointQueryLarge, false);
  const bodyContent = useRef<null | HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [footerScrolled, setFooterScrolled] = useState(false);

  useEffect(() => {
    const content = bodyContent.current;
    if (content && content.offsetHeight >= content.scrollHeight) {
      setFooterScrolled(false);
    }
  }, []);

  const handleScroll = (e: UIEvent<HTMLElement>) => {
    const eventTarget = e.target as HTMLDivElement;

    if (eventTarget.scrollTop !== 0) setScrolled(true);
    else setScrolled(false);

    if (
      Math.floor(eventTarget.scrollHeight - eventTarget.scrollTop) ===
      Math.floor(eventTarget.clientHeight)
    ) {
      setFooterScrolled(false);
    } else {
      setFooterScrolled(true);
    }
  };

  const isSmallModal = size === 'small';
  return (
    <SheetOrModal
      data-testid={testid}
      onDismiss={onDismiss}
      ariaLabel={ariaLabel}
      testid={`${testid}-modal`}
      isOpen={isOpen}
      contentClassName={`${
        isSmallModal ? styles['container-small'] : styles.container
      } ${getVariantClass(size)}`}
      innerContentClassName={`${styles['inner-container']}`}
      overlayClassName={styles.overlay}
      showTouchBar={showTouchBar}
      isSmallModal={isSmallModal}
    >
      <div className={`${className} ${styles.wrapper}`}>
        <div
          className={`${
            isSmallModal
              ? styles['header-wrapper-small']
              : styles['header-wrapper']
          } ${scrolled && styles.scrolled}`}
        >
          <div
            className={`${
              leftIcon || rightIcon || isSmallModal
                ? styles.header
                : styles['header-center']
            } ${isSmallModal && styles['header-small']}`}
          >
            {leftIcon && (
              <button
                className={`${styles['left-icon']} ${styles.icon}`}
                onClick={onLeftIconClick}
                aria-label={`${ariaLabel}-icon-left`}
              >
                {leftIcon}
              </button>
            )}
            {!isDesktop && !leftIcon && !isSmallModal && (
              <div className={styles['empty-icon']}></div>
            )}
            {header === undefined && (
              <Heading
                variant="H4"
                className={headingClassName}
                testid={`${testid}-heading`}
              >
                {heading}
              </Heading>
            )}
            {header}
            {!isDesktop && !rightIcon && !isSmallModal && (
              <div className={styles['empty-icon']}></div>
            )}
            {rightIcon && (
              <button
                className={`${styles['right-icon']} ${styles.icon}`}
                onClick={onRightIconClick}
                aria-label={`${ariaLabel}-icon-right`}
              >
                {rightIcon}
              </button>
            )}
          </div>
          {showXButton && (isDesktop || isSmallModal) && (
            <button
              data-testid={`${testid}-close-button`}
              className={styles.close}
              onClick={onDismiss}
              aria-label={`close ${ariaLabel}`}
            >
              <XIcon />
            </button>
          )}
        </div>
        {body && (
          <div
            className={styles.body}
            onScroll={(e: UIEvent<HTMLElement>) => handleScroll(e)}
            ref={bodyContent}
          >
            {typeof body === 'string' ? (
              <Paragraph testid={`${testid}-body`}>{body}</Paragraph>
            ) : (
              body
            )}
          </div>
        )}
        {confirmButtonText && (
          <div
            className={`${styles.buttons} ${
              footerScrolled && styles['footer-scrolled']
            } ${!stackMobileButtons && styles['buttons-row']} ${
              isSmallModal && styles['buttons-small']
            }`}
          >
            <Button
              style={buttonStyleVariant}
              prominence="primary"
              size="medium"
              onClick={(e) => {
                onActionClick && onActionClick(e);
              }}
              loading={isSubmitting}
              textPrimary={confirmButtonText}
              className={styles.button}
              testid={`${testid}-confirm-button`}
            />
            {secondaryButtonText && !tertiaryButtonText && (
              <Button
                style="default"
                prominence="secondary"
                size="medium"
                onClick={onDismiss}
                textPrimary={secondaryButtonText}
                className={styles.button}
                testid={`${testid}-secondary-confirm-button`}
              />
            )}
            {tertiaryButtonText && !secondaryButtonText && (
              <Button
                style="default"
                prominence="tertiary"
                size="medium"
                onClick={tertiaryButtonAction || onDismiss}
                textPrimary={tertiaryButtonText}
                className={styles.button}
                testid={`${testid}-cancel-button`}
              />
            )}
          </div>
        )}
      </div>
    </SheetOrModal>
  );
}
